import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiAlertCircle, FiEdit, FiTrash2, FiPlus, FiX, FiArrowUp, FiArrowDown, FiCalendar } = FiIcons;

const Standings = () => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeCategory, setActiveCategory] = useState('Senior');
  const [selectedYear, setSelectedYear] = useState('2024-25');
  const [availableYears, setAvailableYears] = useState(['2024-25']);
  const navigate = useNavigate();

  const emptyTeam = {
    position: 1,
    team: '',
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    gf: 0,
    ga: 0,
    points: 0,
    team_category: 'Senior',
    season_year: '2024-25'
  };

  const [formData, setFormData] = useState(emptyTeam);

  const teamCategories = [
    { value: 'Senior', label: 'Α\' Ομάδα (Senior)' },
    { value: 'K17', label: 'Κ17' },
    { value: 'K15', label: 'Κ15' },
    { value: 'K13', label: 'Κ13' }
  ];

  const yearOptions = [
    '2024-25',
    '2023-24',
    '2022-23',
    '2021-22'
  ];

  useEffect(() => {
    initializeStandings();
  }, [activeCategory, selectedYear]);

  const initializeStandings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch existing data for the selected category and year
      const { data: existingData, error: fetchError } = await supabase
        .from('standings_aox1234')
        .select('*')
        .eq('team_category', activeCategory)
        .eq('season_year', selectedYear)
        .order('position', { ascending: true });

      if (fetchError) {
        if (fetchError.code === 'PGRST116' || fetchError.message.includes('does not exist')) {
          console.log('Table does not exist, creating it...');
          await createStandingsTable();
          return;
        } else {
          throw fetchError;
        }
      }

      setStandings(existingData || []);

      // Update available years based on data
      const { data: yearData } = await supabase
        .from('standings_aox1234')
        .select('season_year')
        .eq('team_category', activeCategory);

      if (yearData && yearData.length > 0) {
        const dbYears = yearData
          .map(item => item.season_year)
          .filter(Boolean);

        // Βάλε και το selectedYear μέσα, ώστε να μην "χάνεται" μια νέα σεζόν χωρίς ομάδες ακόμα
        const years = [...new Set([...dbYears, selectedYear])];
        const sortedYears = years.sort().reverse(); // π.χ. ['2025-26', '2024-25', ...]

        setAvailableYears(sortedYears);

        // Αν το τρέχον selectedYear δεν υπάρχει στη λίστα, διάλεξε την πιο πρόσφατη
        setSelectedYear(prev =>
          prev && sortedYears.includes(prev) ? prev : sortedYears[0]
        );
      } else {
        // Αν δεν υπάρχει καθόλου data στη ΒΔ, κράτα τουλάχιστον το selectedYear
        setAvailableYears([selectedYear]);
      }
    } catch (err) {
      console.error('Error initializing standings:', err);
      setError(err.message || 'Failed to initialize standings');
    } finally {
      setLoading(false);
    }
  };

  const createStandingsTable = async () => {
    try {
      console.log('Creating standings table...');

      // Create sample data for Senior category
      const sampleTeams = [
        { position: 1, team: "ΑΟΧ", played: 18, won: 15, drawn: 2, lost: 1, gf: 68, ga: 12, points: 47 },
        { position: 2, team: "Ολυμπιακός Χανίων", played: 18, won: 13, drawn: 3, lost: 2, gf: 45, ga: 18, points: 42 },
        { position: 3, team: "Κισσάμου", played: 18, won: 11, drawn: 1, lost: 6, gf: 38, ga: 25, points: 34 },
        { position: 4, team: "Ρεθύμνου", played: 18, won: 9, drawn: 4, lost: 5, gf: 32, ga: 28, points: 31 },
        { position: 5, team: "Επισκοπή", played: 18, won: 6, drawn: 2, lost: 10, gf: 22, ga: 42, points: 20 },
        { position: 6, team: "Λίντο", played: 18, won: 2, drawn: 1, lost: 15, gf: 15, ga: 65, points: 7 }
      ];

      const { data, error: insertError } = await supabase
        .from('standings_aox1234')
        .insert(sampleTeams.map(team => ({
          ...team,
          team_category: activeCategory,
          season_year: selectedYear,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })));

      if (insertError) {
        console.error('Error creating table with sample data:', insertError);
        throw insertError;
      }

      console.log('Table created successfully with sample data');
      await initializeStandings();
    } catch (err) {
      console.error('Error creating standings table:', err);
      setError(`Failed to create standings table: ${err.message}`);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === 'team' ? value : parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      [name]: parsedValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      if (!formData.team.trim()) {
        throw new Error('Team name is required');
      }

      if (formData.position < 1) {
        throw new Error('Position must be at least 1');
      }

      const teamData = {
        position: parseInt(formData.position),
        team: formData.team.trim(),
        played: parseInt(formData.played) || 0,
        won: parseInt(formData.won) || 0,
        drawn: parseInt(formData.drawn) || 0,
        lost: parseInt(formData.lost) || 0,
        gf: parseInt(formData.gf) || 0,
        ga: parseInt(formData.ga) || 0,
        points: parseInt(formData.points) || 0,
        team_category: activeCategory,
        season_year: selectedYear,
        updated_at: new Date().toISOString()
      };

      let result;
      if (editingId) {
        result = await supabase
          .from('standings_aox1234')
          .update(teamData)
          .eq('id', editingId)
          .select();
      } else {
        result = await supabase
          .from('standings_aox1234')
          .insert([{
            ...teamData,
            created_at: new Date().toISOString()
          }])
          .select();
      }

      if (result.error) {
        console.error('Database error:', result.error);
        throw result.error;
      }

      setMessage({
        type: 'success',
        text: editingId ? 'Η ομάδα ενημερώθηκε επιτυχώς!' : 'Η νέα ομάδα προστέθηκε επιτυχώς!'
      });

      resetForm();
      await initializeStandings();
    } catch (err) {
      console.error('Error saving team:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα: ${err.message || 'Προέκυψε πρόβλημα κατά την αποθήκευση'}`
      });
    }
  };

  const handleEdit = (team) => {
    setFormData({
      position: team.position,
      team: team.team,
      played: team.played || 0,
      won: team.won || 0,
      drawn: team.drawn || 0,
      lost: team.lost || 0,
      gf: team.gf || 0,
      ga: team.ga || 0,
      points: team.points || 0
    });
    setEditingId(team.id);
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή την ομάδα;')) return;

    try {
      const { error } = await supabase
        .from('standings_aox1234')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Η ομάδα διαγράφηκε επιτυχώς!' });
      await initializeStandings();
    } catch (err) {
      console.error('Error deleting team:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα κατά τη διαγραφή: ${err.message}`
      });
    }
  };

  const handleMoveUp = async (id, currentPosition) => {
    try {
      const teamAbove = standings.find(t => t.position === currentPosition - 1);
      if (!teamAbove) return;

      const { error: error1 } = await supabase
        .from('standings_aox1234')
        .update({
          position: currentPosition,
          updated_at: new Date().toISOString()
        })
        .eq('id', teamAbove.id);

      if (error1) throw error1;

      const { error: error2 } = await supabase
        .from('standings_aox1234')
        .update({
          position: currentPosition - 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error2) throw error2;

      await initializeStandings();
    } catch (err) {
      console.error('Error reordering teams:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα κατά την αλλαγή θέσης: ${err.message}`
      });
    }
  };

  const handleMoveDown = async (id, currentPosition) => {
    try {
      const teamBelow = standings.find(t => t.position === currentPosition + 1);
      if (!teamBelow) return;

      const { error: error1 } = await supabase
        .from('standings_aox1234')
        .update({
          position: currentPosition,
          updated_at: new Date().toISOString()
        })
        .eq('id', teamBelow.id);

      if (error1) throw error1;

      const { error: error2 } = await supabase
        .from('standings_aox1234')
        .update({
          position: currentPosition + 1,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error2) throw error2;

      await initializeStandings();
    } catch (err) {
      console.error('Error reordering teams:', err);
      setMessage({
        type: 'error',
        text: `Σφάλμα κατά την αλλαγή θέσης: ${err.message}`
      });
    }
  };

  const resetForm = () => {
    const nextPosition = standings.length > 0 ? Math.max(...standings.map(t => t.position)) + 1 : 1;
    setFormData({
      ...emptyTeam,
      position: nextPosition,
      team_category: activeCategory,
      season_year: selectedYear
    });
    setEditingId(null);
    setShowForm(false);
  };

  const calculateGoalDifference = (gf, ga) => {
    return (gf || 0) - (ga || 0);
  };

  const getTeamCategoryLabel = (category) => {
    const teamCategory = teamCategories.find(cat => cat.value === category);
    return teamCategory ? teamCategory.label : category;
  };

  const createNewSeason = async () => {
    const newYear = prompt('Εισάγετε τη νέα σεζόν (π.χ. 2025-26):');
    if (!newYear) return;

    try {
      // Add the new year to available years if it doesn't exist
      if (!availableYears.includes(newYear)) {
        setAvailableYears(prev => [newYear, ...prev]);
      }
      setSelectedYear(newYear);
      setStandings([]);
      setMessage({
        type: 'success',
        text: `Νέα σεζόν ${newYear} δημιουργήθηκε για την κατηγορία ${getTeamCategoryLabel(activeCategory)}`
      });
    } catch (err) {
      console.error('Error creating new season:', err);
      setMessage({
        type: 'error',
        text: 'Σφάλμα κατά τη δημιουργία νέας σεζόν'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Βαθμολογία</h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center px-4 py-2 ${
                  showForm ? 'bg-gray-600' : 'bg-primary-600'
                } hover:bg-primary-700 text-white rounded-md`}
              >
                <SafeIcon icon={showForm ? FiX : FiPlus} className="w-5 h-5 mr-2" />
                {showForm ? 'Ακύρωση' : 'Προσθήκη Ομάδας'}
              </button>
              <button
                onClick={createNewSeason}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
              >
                <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                Νέα Σεζόν
              </button>
              {standings.length === 0 && (
                <button
                  onClick={createStandingsTable}
                  className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Δημιουργία Δεδομένων
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-md flex items-center ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </motion.div>
        )}

        {/* Category and Year Selection */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Κατηγορία Ομάδας
              </label>
              <div className="flex space-x-2">
                {teamCategories.map(category => (
                  <button
                    key={category.value}
                    onClick={() => {
                      setActiveCategory(category.value);
                      setSelectedYear('2024-25'); // Reset to default year when changing category
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeCategory === category.value
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="year-select" className="block text-sm font-medium text-gray-700 mb-2">
                Σεζόν
              </label>
              <select
                id="year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 rounded-md bg-red-50 text-red-700">
            <p><strong>Σφάλμα:</strong> {error}</p>
            <button
              onClick={() => {
                setError(null);
                initializeStandings();
              }}
              className="mt-2 text-sm underline hover:no-underline"
            >
              Δοκιμάστε ξανά
            </button>
          </div>
        )}

        {/* Form for adding/editing standings */}
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-white rounded-lg shadow-md p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              {editingId ? 'Επεξεργασία Ομάδας' : 'Προσθήκη Νέας Ομάδας'}
              <span className="text-sm font-normal text-gray-600 ml-2">
                ({getTeamCategoryLabel(activeCategory)} - {selectedYear})
              </span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                    Θέση *
                  </label>
                  <input
                    type="number"
                    id="position"
                    name="position"
                    min="1"
                    required
                    value={formData.position}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="team" className="block text-sm font-medium text-gray-700 mb-1">
                    Ομάδα *
                  </label>
                  <input
                    type="text"
                    id="team"
                    name="team"
                    required
                    value={formData.team}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="played" className="block text-sm font-medium text-gray-700 mb-1">
                    Αγώνες *
                  </label>
                  <input
                    type="number"
                    id="played"
                    name="played"
                    min="0"
                    required
                    value={formData.played}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="won" className="block text-sm font-medium text-gray-700 mb-1">
                    Νίκες *
                  </label>
                  <input
                    type="number"
                    id="won"
                    name="won"
                    min="0"
                    required
                    value={formData.won}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="drawn" className="block text-sm font-medium text-gray-700 mb-1">
                    Ισοπαλίες *
                  </label>
                  <input
                    type="number"
                    id="drawn"
                    name="drawn"
                    min="0"
                    required
                    value={formData.drawn}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="lost" className="block text-sm font-medium text-gray-700 mb-1">
                    Ήττες *
                  </label>
                  <input
                    type="number"
                    id="lost"
                    name="lost"
                    min="0"
                    required
                    value={formData.lost}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="gf" className="block text-sm font-medium text-gray-700 mb-1">
                    Γκολ Υπέρ *
                  </label>
                  <input
                    type="number"
                    id="gf"
                    name="gf"
                    min="0"
                    required
                    value={formData.gf}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="ga" className="block text-sm font-medium text-gray-700 mb-1">
                    Γκολ Κατά *
                  </label>
                  <input
                    type="number"
                    id="ga"
                    name="ga"
                    min="0"
                    required
                    value={formData.ga}
                    onChange={handleInputChange}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="points" className="block text-sm font-medium text-gray-700 mb-1">
                  Βαθμοί *
                </label>
                <input
                  type="number"
                  id="points"
                  name="points"
                  required
                  value={formData.points}
                  onChange={handleInputChange}
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Συμβουλή: Οι βαθμοί συνήθως υπολογίζονται ως 3 × Νίκες + 1 × Ισοπαλίες
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {editingId ? 'Ενημέρωση' : 'Αποθήκευση'}
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Standings Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            Βαθμολογία {getTeamCategoryLabel(activeCategory)} - Σεζόν {selectedYear}
          </h2>

          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Φόρτωση...</p>
            </div>
          ) : standings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Θέση
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ομάδα
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Αγ.
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ν
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ι
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Η
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ΓΥ-ΓΚ
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      +/-
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Βαθ.
                    </th>
                    <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ενέργειες
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {standings.map((team) => (
                    <tr key={team.id} className={`${team.team === 'ΑΟΧ' ? 'bg-primary-50' : ''} hover:bg-gray-50`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            team.position === 1 ? 'bg-yellow-100 text-yellow-800' : 
                            team.position <= 3 ? 'bg-green-100 text-green-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {team.position}
                          </div>
                          <div className="flex flex-col ml-3">
                            <button
                              onClick={() => handleMoveUp(team.id, team.position)}
                              disabled={team.position === 1}
                              className={`p-1 ${team.position === 1 ? 'text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              <SafeIcon icon={FiArrowUp} className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleMoveDown(team.id, team.position)}
                              disabled={team.position === standings.length}
                              className={`p-1 ${team.position === standings.length ? 'text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              <SafeIcon icon={FiArrowDown} className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${team.team === 'ΑΟΧ' ? 'text-primary-900 font-bold' : 'text-gray-900'}`}>
                          {team.team}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {team.played || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-green-600 font-semibold">
                        {team.won || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-yellow-600 font-semibold">
                        {team.drawn || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-red-600 font-semibold">
                        {team.lost || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {team.gf || 0}-{team.ga || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                        <span className={`font-semibold ${
                          calculateGoalDifference(team.gf, team.ga) > 0 ? 'text-green-600' : 
                          calculateGoalDifference(team.gf, team.ga) < 0 ? 'text-red-600' : 
                          'text-gray-600'
                        }`}>
                          {calculateGoalDifference(team.gf, team.ga) > 0 ? '+' : ''}{calculateGoalDifference(team.gf, team.ga)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-bold ${team.team === 'ΑΟΧ' ? 'text-primary-600' : 'text-gray-900'}`}>
                          {team.points || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(team)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          <SafeIcon icon={FiEdit} className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(team.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">
                Δεν υπάρχουν καταχωρημένες ομάδες για {getTeamCategoryLabel(activeCategory)} - {selectedYear}
              </p>
              <button
                onClick={createStandingsTable}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md"
              >
                Δημιουργία Βαθμολογίας
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Standings;